import React from 'react';
import { Container, Typography, Box, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { EmailImage } from './EmailImage';
import { SEO } from './SEO';

const contentUseCase = new ContentUseCase();

export const CookiePage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getCookiePolicy(language);

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="cookies" />
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
          {content.title}
        </Typography>

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.what.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.what.description}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.which.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.which.preferences.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.which.preferences.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.which.analytics.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.which.analytics.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.which.tracking.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.which.tracking.description}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.detailed.title}
          </Typography>

          {/* Local storage entries */}
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.detailed.preferences.title}
          </Typography>

          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {content.detailed.preferences.language.title}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.language.purpose}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.language.duration}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.language.legal}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.language.optout}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {content.detailed.preferences.consent.title}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.consent.purpose}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.consent.duration}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.consent.legal}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.preferences.consent.optout}
            </Typography>
          </Box>

          {/* Analytics Cookies */}
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.detailed.analytics.title}
          </Typography>

          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {content.detailed.analytics.googleAnalytics.title}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.analytics.googleAnalytics.purpose}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.analytics.googleAnalytics.duration}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.analytics.googleAnalytics.legal}
            </Typography>
            <Typography variant="body1" paragraph>
              {content.detailed.analytics.googleAnalytics.optout}
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.decline.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', mb: theme.spacing(4) }}>
            {content.decline.description}
          </Typography>
          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <List>
              {content.decline.effects.map((effect, index) => (
                <ListItem key={`decline-effect-${effect.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: 0.5 }}>
                  <ListItemText primary={effect} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: theme.spacing(3), fontStyle: 'italic', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.decline.note}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.manage.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.manage.browser.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.manage.browser.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.manage.note.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.manage.note.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.manage.instructions.title}
          </Typography>
          <Box sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <List>
              {content.manage.instructions.list.map((item, index) => (
                <ListItem key={`instruction-${item.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: 0.5 }}>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.external.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.donations.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.donations.description}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.social.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.external.social.description}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.contact.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.contact.description}
          </Typography>
          <EmailImage language={language} />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {language === 'en' ? 'Last updated:' : 'Stand:'} {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE')}
          </Typography>
        </Box>
      </Container>
  );
};
